export interface ResolvedPlacedImage {
  offsetX: number;
  offsetY: number;
  effectiveScalePercent: number;
  sourceTileSize: number;
  columns: number;
  rows: number;
}

const MIN_SCALE_MULTIPLIER = 0.05;
const MAX_SCALE_MULTIPLIER = 20;

export const isAnimatedGifPath = (path: string): boolean => {
  const normalized = path.trim().toLowerCase();
  return normalized.startsWith('data:image/gif') || /\.gif(?:$|[?#])/i.test(normalized);
};

export const clampPlacedImageOffset = (value: number, tileCount: number): number =>
  Math.max(0, Math.min(tileCount - 1, Math.floor(value)));

export const clampPlacedImageScalePercent = (scalePercent: number): number => {
  const multiplier = Math.max(
    MIN_SCALE_MULTIPLIER,
    Math.min(MAX_SCALE_MULTIPLIER, scalePercent / 100),
  );
  return multiplier * 100;
};

export const resolvePlacedImage = ({
  canvasWidth,
  canvasHeight,
  tileSize,
  imageWidth,
  imageHeight,
  offsetX,
  offsetY,
  scalePercent,
  avoidCropping,
}: {
  canvasWidth: number;
  canvasHeight: number;
  tileSize: number;
  imageWidth: number;
  imageHeight: number;
  offsetX: number;
  offsetY: number;
  scalePercent: number;
  avoidCropping: boolean;
}): ResolvedPlacedImage => {
  const safeOffsetX = clampPlacedImageOffset(offsetX, canvasWidth);
  const safeOffsetY = clampPlacedImageOffset(offsetY, canvasHeight);
  const desiredScale = clampPlacedImageScalePercent(scalePercent) / 100;
  const availableTilesX = Math.max(1, canvasWidth - safeOffsetX);
  const availableTilesY = Math.max(1, canvasHeight - safeOffsetY);
  const fitScale = Math.min(
    (availableTilesX * tileSize) / Math.max(1, imageWidth),
    (availableTilesY * tileSize) / Math.max(1, imageHeight),
  );
  const effectiveScale = avoidCropping ? Math.min(desiredScale, fitScale) : desiredScale;
  const sourceTileSize = tileSize / Math.max(MIN_SCALE_MULTIPLIER, effectiveScale);
  const desiredColumns = Math.max(1, Math.ceil(imageWidth / sourceTileSize));
  const desiredRows = Math.max(1, Math.ceil(imageHeight / sourceTileSize));

  return {
    offsetX: safeOffsetX,
    offsetY: safeOffsetY,
    effectiveScalePercent: effectiveScale * 100,
    sourceTileSize,
    columns: avoidCropping ? Math.min(desiredColumns, availableTilesX) : desiredColumns,
    rows: avoidCropping ? Math.min(desiredRows, availableTilesY) : desiredRows,
  };
};
